import moment from 'moment';

import {
  getCountryByCode,
  getCountryCodeByCountryName,
} from '@/pages/intelligence/utils/commonUtils';

import { metricTitleMap } from '../baseConfig';
export const PLAYER_COUNTRY_DISTRIBUTION_METRIC_KEYS = [
  'alinea__players_by_country',
  'alinea__ps_players_by_country',
  'alinea__wishlists_by_country',
  'gamalytic__players_by_country',
  'players_by_country',
] as const;

/** 勾选中的「玩家国家分布」类指标（顺序与 order_metrics / 页面筛选器一致） */
export function countryPivotMetricKeys(
  metricsCache: Record<string, boolean>,
  orderedMetricKeys: string[] = [],
): string[] {
  const available = PLAYER_COUNTRY_DISTRIBUTION_METRIC_KEYS.filter(m => metricsCache[m]);
  if (!orderedMetricKeys.length) return [...available];
  const ordered = orderedMetricKeys.filter(k =>
    available.includes(k as (typeof PLAYER_COUNTRY_DISTRIBUTION_METRIC_KEYS)[number]),
  );
  const rest = available.filter(k => !ordered.includes(k));
  return [...ordered, ...rest];
}

/** 勾选中的其余指标（单列展示），顺序与 order_metrics / 页面筛选器一致 */
export function flatMetricKeys(
  metricsCache: Record<string, boolean>,
  orderedMetricKeys: string[] = [],
): string[] {
  const available = Object.keys(metricsCache || {}).filter(
    k =>
      metricsCache[k] &&
      !(PLAYER_COUNTRY_DISTRIBUTION_METRIC_KEYS as readonly string[]).includes(
        k,
      ),
  );
  if (!orderedMetricKeys.length) return available;
  const ordered = orderedMetricKeys.filter(k => available.includes(k));
  const rest = available.filter(k => !ordered.includes(k));
  return [...ordered, ...rest];
}

/** 与明细页一致：优先 metrics_*，其次 *_segment_All（若存在） */
function pickMetricValueForPivot(row: any, metricKey: string): unknown {
  const base = `metrics_${metricKey}`;
  const direct = row[base];
  const segAll = row[`${base}_segment_All`];
  if (direct !== undefined && direct !== null && direct !== '') {
    return direct;
  }
  if (segAll !== undefined && segAll !== null && segAll !== '') {
    return segAll;
  }
  return direct ?? segAll ?? '';
}

function pickMetricGrowthForPivot(row: any, metricKey: string): unknown {
  const base = `metrics_${metricKey}_growth`;
  const direct = row[base];
  const segAll = row[`metrics_${metricKey}_segment_All_growth`];
  if (direct !== undefined && direct !== null && direct !== '') {
    return direct;
  }
  if (segAll !== undefined && segAll !== null && segAll !== '') {
    return segAll;
  }
  return direct ?? segAll ?? '';
}

function formatPlayerCountryPercentForPivot(value: unknown): string {
  if (value === undefined || value === null || value === '') {
    return '';
  }

  const valueNumber = Number.parseFloat(String(value).replace('%', ''));
  if (!Number.isFinite(valueNumber)) {
    return '';
  }

  return `${valueNumber.toFixed(2)}%`;
}

function isTotalMarketForPivot(marketCode: unknown): boolean {
  return String(marketCode).toLowerCase() === 'total';
}

function normalizeMarketCodeForPivot(market: unknown, regionOptions: any[]): string {
  if (market === undefined || market === null || market === '') {
    return '';
  }

  const marketStr = String(market);
  if (isTotalMarketForPivot(marketStr)) {
    return 'total';
  }

  const countryByCode = getCountryByCode(marketStr, regionOptions);
  if (countryByCode?.country_code) {
    return countryByCode.country_code.toLowerCase();
  }

  return getCountryCodeByCountryName(regionOptions, marketStr).toLowerCase();
}

/** Total 列置于各国之前；其余按英文国名排序 */
function orderMarketsForPivotHeaders(
  markets: string[],
  regionOptions: any[],
): string[] {
  const totals = ['total'];
  const others = markets.filter(m => !isTotalMarketForPivot(m));
  others.sort((a, b) => {
    const ea = getCountryByCode(a, regionOptions)?.country_name_en || a;
    const eb = getCountryByCode(b, regionOptions)?.country_name_en || b;
    return ea.localeCompare(eb);
  });
  return [...totals, ...others];
}

/**
 * 参考格式：`Gamalytic Steam Players by Country (Total)`、`Gamalytic Steam Players by Country(cn)` —— 指标名全拼自 metricTitleMap。
 */
export function columnTitleForMarketPivot(
  metricTitleFull: string,
  marketCode: string,
): string {
  if (String(marketCode).toLowerCase() === 'total') {
    return `${metricTitleFull} (Total)`;
  }
  return `${metricTitleFull}(${String(marketCode).toLowerCase()})`;
}

/** 国家分布指标无 date，分组时不拆时间 */
function groupKeyForCountrySnapshot(row: any, params: any): string {
  const r = params.b_group_by_region ? String(row?.region ?? '') : '_r';

  const p = params.b_group_by_device
    ? String(row?.platform ?? 'Aggregated')
    : '_p';

  return `_snapshot@@@${r}@@@${p}`;
}

/** 普通指标按日期（及区域 / 平台）分组 */
function groupKeyForFlatMetrics(row: any, params: any): string {
  const d = params.b_group_by_date ? String(row?.date ?? '') : '_d';

  const r = params.b_group_by_region ? String(row?.region ?? '') : '_r';

  const p = params.b_group_by_device
    ? String(row?.platform ?? 'Aggregated')
    : '_p';

  return `${d}@@@${r}@@@${p}`;
}

/** @deprecated 仅用于时间汇总场景（国家分布与普通指标同一行） */
function groupKeyForPivot(row: any, params: any): string {
  const d = params.b_group_by_date ? String(row?.date ?? '') : '_d';

  const r = params.b_group_by_region ? String(row?.region ?? '') : '_r';

  const p = params.b_group_by_device
    ? String(row?.platform ?? 'Aggregated')
    : '_p';

  return `${d}@@@${r}@@@${p}`;
}

/** 分日期 + 混选：国家占比单独一行（Date 为空），普通指标按日期分行 */
function shouldSplitCountrySnapshotFromMixedMetrics(
  params: any,
  countryMetricNames: string[],
  flatMetricNames: string[],
): boolean {
  return (
    Boolean(params?.b_group_by_date) &&
    countryMetricNames.length > 0 &&
    flatMetricNames.length > 0
  );
}

/** 不分日期 + 混选：国家占比与普通指标合并为一行 */
function shouldMergeCountryAndFlatOnSingleRow(
  params: any,
  countryMetricNames: string[],
  flatMetricNames: string[],
): boolean {
  return (
    !params?.b_group_by_date &&
    countryMetricNames.length > 0 &&
    flatMetricNames.length > 0
  );
}

function buildCountrySnapshotBaseRow(
  row: any,
  params: any,
): Record<string, any> {
  const base: Record<string, any> = {
    _isCountrySnapshot: true,
    date: '',
  };

  if (params.b_group_by_device) {
    base.platform = row?.platform ?? 'Aggregated';
  }

  return base;
}

function buildFlatMetricBaseRow(row: any, params: any): Record<string, any> {
  const base: Record<string, any> = {
    _isCountrySnapshot: false,
  };

  if (params.b_group_by_date) {
    base.date = row?.date ?? '';
  }

  if (params.b_group_by_device) {
    base.platform = row?.platform ?? 'Aggregated';
  }

  return base;
}

const FLAT_METRIC_PREFERRED_MARKETS = ['total', 'global'];

function pickFlatMetricFromRows(
  rows: any[],
  metricKey: string,
  pickValue: (row: any, metricKey: string) => unknown,
): unknown {
  for (const preferredMarket of FLAT_METRIC_PREFERRED_MARKETS) {
    const preferredRow = rows.find(
      row => String(row?.market).toLowerCase() === preferredMarket,
    );
    if (!preferredRow) continue;
    const preferredValue = pickValue(preferredRow, metricKey);
    if (
      preferredValue !== undefined &&
      preferredValue !== null &&
      preferredValue !== ''
    ) {
      return preferredValue;
    }
  }

  for (const row of rows) {
    const value = pickValue(row, metricKey);
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
  }

  return '';
}

/** 非国家分布指标：优先 total / global 行，否则取首个有值的 market 行 */
function aggregateFlatMetricRow(
  rows: any[],
  metricKey: string,
): unknown {
  return pickFlatMetricFromRows(rows, metricKey, pickMetricValueForPivot);
}

function aggregateFlatGrowthRow(
  rows: any[],
  metricKey: string,
): unknown {
  return pickFlatMetricFromRows(rows, metricKey, pickMetricGrowthForPivot);
}

/**
 * 分国家且勾选了三类「玩家国家分布」之一时启用混合横表；
 * 若仅勾选其它指标仍走表格默认竖表（不调 pivot）。
 */
export function shouldPivotPlayersByCountry(
  metricsCache: Record<string, boolean>,
  params: any,
): boolean {
  if (!params?.b_group_by_market) {
    return false;
  }
  return countryPivotMetricKeys(metricsCache).length > 0;
}

export function pivotPlayersByCountryTargetList(
  targetList: any[],
  metricsCache: Record<string, boolean>,
  params: any,
  regionOptions: any[],
  isRatioShow: boolean,
  orderedMetricKeys: string[] = [],
): {
  pivoted: any[];
  markets: string[];
  countryMetricNames: string[];
  flatMetricNames: string[];
} {
  const countryMetricNames = countryPivotMetricKeys(
    metricsCache,
    orderedMetricKeys,
  );
  const flatMetricNames = flatMetricKeys(metricsCache, orderedMetricKeys);

  const splitSnapshot = shouldSplitCountrySnapshotFromMixedMetrics(
    params,
    countryMetricNames,
    flatMetricNames,
  );
  const mergeSingleRow = shouldMergeCountryAndFlatOnSingleRow(
    params,
    countryMetricNames,
    flatMetricNames,
  );

  const marketSet = new Set<string>();
  let pivoted: any[] = [];
  const snapshotMap = new Map<string, any>();
  const flatMap = new Map<string, any>();
  const flatRowsByGroup = new Map<string, any[]>();

  if (splitSnapshot) {
    for (const row of targetList) {
      const snapshotKey = groupKeyForCountrySnapshot(row, params);

      if (!snapshotMap.has(snapshotKey)) {
        snapshotMap.set(snapshotKey, buildCountrySnapshotBaseRow(row, params));
      }

      const snapshotRow = snapshotMap.get(snapshotKey);
      const mkt = normalizeMarketCodeForPivot(row?.market, regionOptions);

      if (!mkt) {
        continue;
      }

      marketSet.add(mkt);

      for (const metricName of countryMetricNames) {
        snapshotRow[`_pbc_val__${metricName}__${mkt}`] =
          pickMetricValueForPivot(row, metricName);

        if (isRatioShow) {
          snapshotRow[`_pbc_gr__${metricName}__${mkt}`] =
            pickMetricGrowthForPivot(row, metricName);
        }
      }
    }

    for (const row of targetList) {
      if (params.b_group_by_date && !row?.date) {
        continue;
      }

      const flatKey = groupKeyForFlatMetrics(row, params);

      if (!flatMap.has(flatKey)) {
        flatMap.set(flatKey, buildFlatMetricBaseRow(row, params));
      }

      if (!flatRowsByGroup.has(flatKey)) {
        flatRowsByGroup.set(flatKey, []);
      }
      flatRowsByGroup.get(flatKey)!.push(row);
    }

    for (const [flatKey, flatRow] of flatMap.entries()) {
      const rows = flatRowsByGroup.get(flatKey) || [];

      for (const fk of flatMetricNames) {
        flatRow[`_flat_val__${fk}`] = aggregateFlatMetricRow(rows, fk);

        if (isRatioShow) {
          flatRow[`_flat_gr__${fk}`] = aggregateFlatGrowthRow(rows, fk);
        }
      }
    }

    const snapshotRows = Array.from(snapshotMap.values());
    const datedRows = Array.from(flatMap.values());

    datedRows.sort((a: any, b: any) => {
      if (a.date !== b.date) {
        return moment(b.date).diff(moment(a.date));
      }
      return String(a.platform ?? '').localeCompare(String(b.platform ?? ''));
    });

    pivoted = [...snapshotRows, ...datedRows];
  } else if (mergeSingleRow) {
    const mergedRow = buildCountrySnapshotBaseRow(targetList[0] || {}, params);
    mergedRow._isMergedNoDateRow = true;

    for (const row of targetList) {
      const mkt = normalizeMarketCodeForPivot(row?.market, regionOptions);

      if (!mkt) {
        continue;
      }

      marketSet.add(mkt);

      for (const metricName of countryMetricNames) {
        mergedRow[`_pbc_val__${metricName}__${mkt}`] =
          pickMetricValueForPivot(row, metricName);

        if (isRatioShow) {
          mergedRow[`_pbc_gr__${metricName}__${mkt}`] =
            pickMetricGrowthForPivot(row, metricName);
        }
      }
    }

    for (const fk of flatMetricNames) {
      mergedRow[`_flat_val__${fk}`] = aggregateFlatMetricRow(targetList, fk);

      if (isRatioShow) {
        mergedRow[`_flat_gr__${fk}`] = aggregateFlatGrowthRow(targetList, fk);
      }
    }

    pivoted = [mergedRow];
  } else {
    const rowsByGroup = new Map<string, any[]>();
    const groupMap = new Map<string, any>();
    const onlyCountryMetrics = countryMetricNames.length > 0 && !flatMetricNames.length;

    for (const row of targetList) {
      const gk = onlyCountryMetrics
        ? groupKeyForCountrySnapshot(row, params)
        : groupKeyForPivot(row, params);

      if (!rowsByGroup.has(gk)) {
        rowsByGroup.set(gk, []);
      }
      rowsByGroup.get(gk)!.push(row);
    }

    for (const row of targetList) {
      const gk = onlyCountryMetrics
        ? groupKeyForCountrySnapshot(row, params)
        : groupKeyForPivot(row, params);

      if (!groupMap.has(gk)) {
        const base: any = {
          _isCountrySnapshot: onlyCountryMetrics,
        };

        if (params.b_group_by_date && !onlyCountryMetrics) {
          base.date = row.date;
        } else if (params.b_group_by_date && onlyCountryMetrics) {
          base.date = '';
        }

        if (params.b_group_by_device) {
          base.platform = row.platform ?? 'Aggregated';
        }

        groupMap.set(gk, base);
      }

      const g = groupMap.get(gk);
      const mkt = normalizeMarketCodeForPivot(row?.market, regionOptions);

      if (!mkt) {
        continue;
      }

      marketSet.add(mkt);

      for (const metricName of countryMetricNames) {
        g[`_pbc_val__${metricName}__${mkt}`] = pickMetricValueForPivot(
          row,
          metricName,
        );

        if (isRatioShow) {
          g[`_pbc_gr__${metricName}__${mkt}`] = pickMetricGrowthForPivot(
            row,
            metricName,
          );
        }
      }
    }

    for (const [gk, g] of groupMap.entries()) {
      const rows = rowsByGroup.get(gk) || [];

      for (const fk of flatMetricNames) {
        g[`_flat_val__${fk}`] = aggregateFlatMetricRow(rows, fk);

        if (isRatioShow) {
          g[`_flat_gr__${fk}`] = aggregateFlatGrowthRow(rows, fk);
        }
      }
    }

    pivoted = Array.from(groupMap.values());

    pivoted.sort((a: any, b: any) => {
      if (params.b_group_by_date && a.date !== b.date) {
        if (!a.date) return -1;
        if (!b.date) return 1;
        return moment(b.date).diff(moment(a.date));
      }
      const pa = String(a.platform ?? '');
      const pb = String(b.platform ?? '');
      return pa.localeCompare(pb);
    });
  }

  targetList.forEach(r => {
    const marketCode = normalizeMarketCodeForPivot(r?.market, regionOptions);
    if (marketCode) {
      marketSet.add(marketCode);
    }
  });

  const markets = orderMarketsForPivotHeaders(
    Array.from(marketSet),
    regionOptions,
  );

  return {
    pivoted,
    markets,
    countryMetricNames,
    flatMetricNames,
  };
}

/** 非 pivot 竖表：将国家分布类指标列（含对应 % 列）挪到所有普通指标列之后 */
export function moveCountryDistributionStatColumnsLast(statConfig: any[]): any[] {
  const regular: any[] = [];
  const country: any[] = [];

  for (const col of statConfig) {
    const keySource = col?.key?.toString() || '';
    const isCountryCol = PLAYER_COUNTRY_DISTRIBUTION_METRIC_KEYS.some(metricKey =>
      keySource.includes(`metrics_${metricKey}`),
    );
    if (isCountryCol) {
      country.push(col);
    } else {
      regular.push(col);
    }
  }

  return [...regular, ...country];
}

function formatPivotDateCell(item: any, params: any): string {
  const rawDate = item?.date;
  if (!rawDate || item?._isCountrySnapshot) {
    return '';
  }
  if (params?.granularity === 'monthly') {
    const formatted = moment(rawDate).format('YYYY-MM');
    return formatted === 'Invalid date' ? '' : formatted;
  }
  return String(rawDate);
}

function appendDimensionColumns(
  cols: any[],
  params: any,
  options: { includeGameName: boolean; gameName: string },
): void {
  const { includeGameName, gameName } = options;

  if (includeGameName) {
    cols.push({
      title: 'Game Name',
      key: () => gameName,
      when: () => true,
    });
  }

  if (params.b_group_by_date) {
    cols.push({
      title: 'Date',
      key: (item: any) => formatPivotDateCell(item, params),
      when: () => true,
    });
  }

  if (params.b_group_by_device) {
    cols.push({
      title: 'Platform',
      key: (item: any) => item?.platform || '',
      when: () => true,
    });
  }
}

function appendFlatMetricColumns(
  cols: any[],
  flatMetricKeyList: string[],
  isRatioShow: boolean,
  titleFor: (k: string) => string,
): void {
  for (const fk of flatMetricKeyList) {
    const mtFull = titleFor(fk);

    cols.push({
      title: mtFull,
      key: (item: any) =>
        item?._isCountrySnapshot && !item?._isMergedNoDateRow
          ? ''
          : item[`_flat_val__${fk}`] ?? '',
      when: () => true,
      metricKey: fk,
    });

    if (isRatioShow) {
      cols.push({
        title: '%',
        key: (item: any) =>
          item?._isCountrySnapshot && !item?._isMergedNoDateRow
            ? ''
            : item[`_flat_gr__${fk}`] ?? '',
        when: () => true,
        metricKey: fk,
      });
    }
  }
}

function appendCountryPivotColumns(
  cols: any[],
  countryMetricKeyList: string[],
  flatMetricKeyList: string[],
  markets: string[],
  regionOptions: any[],
  options: {
    useDateByCountryLayout: boolean;
    singleCountryMetric: boolean;
  },
  titleFor: (k: string) => string,
): void {
  for (const metricKey of countryMetricKeyList) {
    const mtFull = titleFor(metricKey);

    for (const mkt of markets) {
      const headerBase = getMarketPivotColumnTitle(mtFull, mkt, regionOptions, {
        useDateByCountryLayout: options.useDateByCountryLayout,
        singleCountryMetric: options.singleCountryMetric,
      });

      cols.push({
        title: headerBase,
        key: (item: any) => {
          if (
            flatMetricKeyList.length > 0 &&
            !item?._isCountrySnapshot &&
            !item?._isMergedNoDateRow
          ) {
            return '';
          }
          return isTotalMarketForPivot(mkt)
            ? '100.00%'
            : formatPlayerCountryPercentForPivot(
                item[`_pbc_val__${metricKey}__${mkt}`],
              );
        },
        when: () => true,
      });
    }
  }
}

/**
 * 详情页「分日期 + 分国家」：Date 为行、各国为列的宽表。
 */
export function isDetailDateByCountryPivotFormat(params: any): boolean {
  return Boolean(params?.b_group_by_date && params?.b_group_by_market);
}

export function getMarketPivotColumnTitle(
  metricTitleFull: string,
  marketCode: string,
  regionOptions: any[],
  options: {
    useDateByCountryLayout: boolean;
    singleCountryMetric: boolean;
  },
): string {
  const { useDateByCountryLayout, singleCountryMetric } = options;

  if (isTotalMarketForPivot(marketCode)) {
    if (useDateByCountryLayout && singleCountryMetric) {
      return 'Total';
    }
    return columnTitleForMarketPivot(metricTitleFull, marketCode);
  }

  if (useDateByCountryLayout && singleCountryMetric) {
    const country = getCountryByCode(marketCode, regionOptions);
    return country?.country_name_en || String(marketCode).toLowerCase();
  }

  return columnTitleForMarketPivot(metricTitleFull, marketCode);
}

/**
 * 维度列 → 普通指标列 → 国家分布宽列（国家分布始终在最后）。
 * 分日期 + 分国家：Date | 其他指标 | Total | 各国英文名列。
 */
export function buildPlayersCountryPivotStatConfig(
  params: any,
  markets: string[],
  isRatioShow: boolean,
  countryMetricKeyList: string[],
  flatMetricKeyList: string[],
  gameName: string,
  regionOptions: any[] = [],
): any[] {
  const cols: any[] = [];

  const titleFor = (k: string) =>
    (metricTitleMap as Record<string, string>)[k] || k;

  const useDateByCountryLayout = isDetailDateByCountryPivotFormat(params);
  const singleCountryMetric = countryMetricKeyList.length === 1;

  appendDimensionColumns(cols, params, {
    includeGameName: !useDateByCountryLayout,
    gameName,
  });

  appendFlatMetricColumns(cols, flatMetricKeyList, isRatioShow, titleFor);

  appendCountryPivotColumns(
    cols,
    countryMetricKeyList,
    flatMetricKeyList,
    markets,
    regionOptions,
    { useDateByCountryLayout, singleCountryMetric },
    titleFor,
  );

  return cols;
}
